import { Tabs as MuiTabs, TabsProps as MuiTabsProps, styled } from '@mui/material';
import React, { ChangeEvent } from 'react';

import { colorSystem, h4, h5 } from '../../theme';
import { iconSlot } from '../icons/iconSlot';

export type TabsProps<T> = Omit<MuiTabsProps, 'onChange' | 'value'> & {
    size: 'large' | 'small';
    value: T;
    // Omitting onChange from MuiTabsProps to fix a bug in the MUI 4.11 types
    // https://github.com/mui/material-ui/issues/17454
    onChange?: (event: ChangeEvent<{}>, value: T) => void;
};

function TabsInner<T>(
    { size, onChange, children, ...rest }: TabsProps<T>,
    ref: React.ForwardedRef<HTMLDivElement>,
): JSX.Element {
    // Our (event, T) handler isn't assignable to MUI's, so a spread makes T unknown; MUI passes the tab's `value` as T.
    const muiOnChange =
        onChange === undefined
            ? undefined
            : (event: React.SyntheticEvent, value: unknown) => {
                  onChange(event as ChangeEvent<{}>, value as T);
              };

    switch (size) {
        case 'small':
            return (
                <SmallTabs ref={ref} {...rest} onChange={muiOnChange}>
                    {children}
                </SmallTabs>
            );
        case 'large':
            return (
                <LargeTabs ref={ref} {...rest} onChange={muiOnChange}>
                    {children}
                </LargeTabs>
            );
    }
}

const TabsForward = React.forwardRef(TabsInner);

// forwardRef drops generics (https://github.com/microsoft/TypeScript/issues/58467); the cast restores T, so `onChange` isn't `unknown`.
export const Tabs = TabsForward as <T>(
    props: TabsProps<T> & React.RefAttributes<HTMLDivElement>,
) => React.ReactElement | null;

const DefaultTabs = styled(MuiTabs)({
    borderBottom: `1px solid ${colorSystem.neutral[2]}`,
    '& .MuiTab-root': {
        padding: '0px 0px 10px 0px',
    },
    '& .MuiTab-root:hover': {
        paddingBottom: '8px',
        borderBottom: `2px solid ${colorSystem.neutral[6]}`,
    },
    '& .MuiTabs-indicator': {
        background: colorSystem.neutral[9],
    },
});

// Sizes a tab's `icon`, icons in its label and the scroll arrows; a spinner in a label keeps its own size.
const SmallTabs = styled(DefaultTabs)({
    '& *': {
        ...h5,
    },
    ...iconSlot(16, { reach: 'grandchild' }),
    '& .MuiTabs-list': {
        gap: '24px',
    },
});

const LargeTabs = styled(DefaultTabs)({
    '& *': {
        ...h4,
    },
    ...iconSlot(20, { reach: 'grandchild' }),
    '& .MuiTabs-list': {
        gap: '32px',
    },
});
